// Language Switcher Component
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { LANGUAGES, changeLanguage, getLanguageInfo } from '../i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LanguageSwitcher = ({ compact = false }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  
  const currentLang = getLanguageInfo(i18n.language);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
    // Force re-render to apply translations
    window.location.reload();
  };

  if (compact) {
    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger className="flex items-center gap-1 text-xs hover:text-emerald-600 transition-colors px-2 py-1 rounded hover:bg-slate-100">
          <Globe className="h-3 w-3" />
          <span className="font-medium">{currentLang.code.toUpperCase()}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
          <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase sticky top-0 bg-white border-b">
            {t('language.select', 'Select Language')}
          </div>
          {LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`flex items-center justify-between cursor-pointer ${
                i18n.language === lang.code ? 'bg-emerald-50 text-emerald-700' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{lang.flag}</span>
                <div className="flex flex-col">
                  <span className="font-medium">{lang.nativeName}</span>
                  <span className="text-xs text-slate-400">{lang.name}</span>
                </div>
              </div>
              {i18n.language === lang.code && (
                <Check className="h-4 w-4 text-emerald-600" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all">
        <span className="text-lg">{currentLang.flag}</span>
        <span className="text-sm font-medium">{currentLang.nativeName}</span>
        <Globe className="h-4 w-4 text-slate-400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 max-h-96 overflow-y-auto">
        <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase sticky top-0 bg-white border-b">
          {t('language.select', 'Select Language')} ({LANGUAGES.length})
        </div>
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`flex items-center justify-between cursor-pointer py-2 ${
              i18n.language === lang.code ? 'bg-emerald-50 text-emerald-700' : ''
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">{lang.flag}</span>
              <div className="flex flex-col">
                <span className="font-medium">{lang.nativeName}</span>
                <span className="text-xs text-slate-400">{lang.name}</span>
              </div>
            </div>
            {i18n.language === lang.code && (
              <Check className="h-5 w-5 text-emerald-600" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
